import {authFetch} from "../../authentication/authFetch";

const API_URL = process.env.REACT_APP_API_URL;

function getAuthHeaders(authToken) {
    if (!authToken) {
        throw new Error('Missing auth token');
    }

    return {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
    };
}

export async function fetchKnowledgeFeedEntries(authToken) {
    const response = await authFetch(`${API_URL}/api/knowledge/feed/entries`, {
        method: 'GET',
        headers: getAuthHeaders(authToken),
    });

    if (!response.ok) {
        throw new Error('Failed to fetch knowledge feed entries');
    }

    const payload = await response.json();
    return Array.isArray(payload) ? payload : [];
}

export async function fetchKnowledgeFollowingContents(authToken) {
    const response = await authFetch(`${API_URL}/api/knowledge/following/contents`, {
        method: 'GET',
        headers: getAuthHeaders(authToken),
    });

    if (!response.ok) {
        throw new Error('Failed to fetch followed contents');
    }

    const payload = await response.json();
    return Array.isArray(payload) ? payload : [];
}

function normalizeFeedPage(payload) {
    return {
        items: Array.isArray(payload?.items) ? payload.items : [],
        nextCursor: payload?.next_cursor ?? payload?.nextCursor ?? null,
        hasMore: payload?.has_more ?? payload?.hasMore ?? false,
    };
}

export async function addKnowledgeRssSource(authToken, rssUrl, cursor = null) {
    const response = await authFetch(`${API_URL}/api/knowledge/feed/fetch`, {
        method: 'POST',
        headers: getAuthHeaders(authToken),
        body: JSON.stringify({rss_url: rssUrl, cursor}),
    });

    if (!response.ok) {
        throw new Error('Failed to add RSS source');
    }

    return normalizeFeedPage(await response.json());
}

export async function refreshAllFeeds(authToken, cursor = null) {
    const response = await authFetch(`${API_URL}/api/knowledge/feed/refresh-all`, {
        method: 'POST',
        headers: getAuthHeaders(authToken),
        body: JSON.stringify({cursor}),
    });

    if (!response.ok) {
        const error = new Error('Failed to refresh all feeds');
        error.status = response.status;
        throw error;
    }

    return normalizeFeedPage(await response.json());
}

export async function saveKnowledgeFeedEntry(authToken, entry) {
    const response = await authFetch(`${API_URL}/api/knowledge/feed/entries/save`, {
        method: 'POST',
        headers: getAuthHeaders(authToken),
        body: JSON.stringify({
            title: entry.title,
            summary: entry.summary ?? null,
            text: entry.text ?? null,
            source_url: entry.source_url ?? entry.sourceUrl,
            fetched_source: entry.fetched_source ?? entry.fetchedSource,
            image_url: entry.image_url ?? entry.imageUrl ?? null,
            published_at: entry.published_at ?? entry.publishedAt ?? null,
        }),
    });

    if (!response.ok) {
        throw new Error('Failed to save feed entry');
    }

    return response.json();
}

export async function unfollowKnowledgeFeed(authToken, rssUrl) {
    const response = await authFetch(`${API_URL}/api/knowledge/following/feed`, {
        method: 'DELETE',
        headers: getAuthHeaders(authToken),
        body: JSON.stringify({rss_url: rssUrl, rssUrl}),
    });

    if (!response.ok) {
        throw new Error('Failed to delete feed source');
    }

    return null;
}

export async function fetchKnowledgeTopics(authToken) {
    const response = await authFetch(`${API_URL}/api/knowledge/topics`, {
        method: 'GET',
        headers: getAuthHeaders(authToken),
    });

    if (!response.ok) {
        throw new Error('Failed to fetch knowledge topics');
    }

    const payload = await response.json();
    return Array.isArray(payload) ? payload : [];
}

export async function addKnowledgeTopic(authToken, topic) {
    const response = await authFetch(`${API_URL}/api/knowledge/topics`, {
        method: 'POST',
        headers: getAuthHeaders(authToken),
        body: JSON.stringify(topic),
    });

    if (!response.ok) {
        throw new Error('Failed to create knowledge topic');
    }

    return response.json();
}

export async function fetchSavedKnowledgeLessons(authToken) {
    const response = await authFetch(`${API_URL}/api/knowledge/lessons/saved`, {
        method: 'GET',
        headers: getAuthHeaders(authToken),
    });

    if (!response.ok) {
        throw new Error('Failed to fetch saved knowledge lessons');
    }

    const payload = await response.json();
    return Array.isArray(payload) ? payload : [];
}

export async function generateKnowledgeLesson(authToken, topicId) {
    const response = await authFetch(`${API_URL}/api/knowledge/lessons/fetch`, {
        method: 'POST',
        headers: getAuthHeaders(authToken),
        body: JSON.stringify({topic_id: topicId}),
    });

    if (!response.ok) {
        throw new Error('Failed to generate knowledge lesson');
    }

    return response.json();
}

export async function saveKnowledgeLesson(authToken, lesson) {
    const response = await authFetch(`${API_URL}/api/knowledge/lessons/save`, {
        method: 'POST',
        headers: getAuthHeaders(authToken),
        body: JSON.stringify({
            topic_id: lesson.topic_id ?? lesson.topicId,
            title: lesson.title,
            why_it_matters: lesson.why_it_matters ?? lesson.whyItMatters,
            explanation: lesson.explanation,
            example_title: lesson.example_title ?? lesson.exampleTitle,
            example_content: lesson.example_content ?? lesson.exampleContent,
            example_language: lesson.example_language ?? lesson.exampleLanguage,
            diagram: lesson.diagram,
            senior_insight: lesson.senior_insight ?? lesson.seniorInsight,
            common_mistake: lesson.common_mistake ?? lesson.commonMistake,
            check_question: lesson.check_question ?? lesson.checkQuestion,
            check_answer: lesson.check_answer ?? lesson.checkAnswer,
            sources: lesson.sources ?? [],
            generated_at: lesson.generated_at ?? lesson.generatedAt,
        }),
    });

    if (!response.ok) {
        throw new Error('Failed to save knowledge lesson');
    }

    return response.json();
}
